"use client";

import React, { useState } from "react";
import { Star, ChevronDown, Quote, MessageSquareHeart } from "lucide-react";
import { cn } from "@/lib/utils";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
const reviews = [{
  id: 1,
  name: "Joshua Manzanera",
  avatar: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1572292/img/avatar_andrzej_k.png",
  date: "2 weeks ago",
  rating: 5,
  fullText: "Jamie was quick, professional and detailed in his feedback on what my next steps were for maintaining my yard.",
  isLong: true
}, {
  id: 2,
  name: "Erika Phillips",
  avatar: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1572292/img/avatar_lukasz_g.png",
  date: "1 month ago",
  rating: 5,
  fullText: "Fast response time and a very competitive quote. They handled the hedge trimming and edging with precision. It's rare to find such professional property maintenance these days. 5 stars for the team!",
  isLong: false
}, {
  id: 3,
  name: "Sam",
  avatar: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1572292/img/avatar_katarzyna_o.png",
  date: "2 months ago",
  rating: 5,
  fullText: "Jamie fantastic with his services very high end and great pricing for what he does, great communication and very kind and prompt with his actions, He was also flexible with his time.",
  isLong: true
}, {
  id: 4,
  name: "Vince Raco",
  avatar: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1572292/img/avatar_krzysztof_r.png",
  date: "3 months ago",
  rating: 5,
  fullText: "The best lawn mowing service I've used in years. They show up when they say they will and the attention to detail around the garden beds is fantastic. My lawn has never looked healthier.",
  isLong: false
}];
function ReviewCard({
  review,
  index
}: {
  review: typeof reviews[0];
  index: number;
}) {
  const [isOpen, setIsOpen] = useState(false);
  return <Card data-index={index} className="relative h-full flex flex-col bg-zinc-900/80 backdrop-blur-md border border-white/10 shadow-xl shadow-black/50 rounded-2xl overflow-hidden transition-all duration-300 hover:shadow-primary/20 hover:bg-zinc-900 group text-white">
      <div className="absolute top-6 right-6 text-emerald-500/20 transition-transform duration-500 group-hover:scale-110 group-hover:text-emerald-500/30">
        <Quote className="size-10" />
      </div>
      
      <CardHeader className="pb-4 relative z-10">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-center gap-4">
            
            <div>
              <h4 className="font-bold text-white leading-none mb-1.5 tracking-tight">
                {review.name}
              </h4>
              <p className="text-xs text-zinc-400 font-medium">{review.date}</p>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-1 mt-3">
          {[...Array(5)].map((_, i) => <Star key={i} data-index={i} className={cn("size-4", i < review.rating ? "fill-emerald-500 text-emerald-500" : "fill-muted text-muted")} />)}
        </div>
      </CardHeader>

      <CardContent className="flex-grow relative z-10">
        {review.isLong ? <Collapsible open={isOpen} onOpenChange={setIsOpen} className="w-full">
            <div className={cn("text-zinc-300 text-sm md:text-base leading-relaxed transition-all duration-300 font-medium", !isOpen && "line-clamp-3")}>
              {review.fullText}
            </div>
            <CollapsibleTrigger asChild>
              <Button variant="ghost" size="sm" className="mt-2 text-emerald-500 hover:text-emerald-400 hover:bg-emerald-500/10 p-0 h-auto font-bold uppercase tracking-wider text-xs flex items-center gap-1">
                {isOpen ? "Read Less" : "Read More"}
                <ChevronDown className={cn("size-3 transition-transform duration-300", isOpen && "rotate-180")} />
              </Button>
            </CollapsibleTrigger>
          </Collapsible> : <p className="text-zinc-300 text-sm md:text-base leading-relaxed font-medium">
            {review.fullText}
          </p>}
      </CardContent>
    </Card>;
}
export default function Comments() {
  return <section data-section-id="153827" id="testimonial" className="w-full py-20 md:py-32 bg-zinc-950 text-white relative overflow-hidden">
      {}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-secondary/20 rounded-full blur-[100px] pointer-events-none" />

      <div className="container mx-auto px-4 md:px-6 relative z-10">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-8 mb-12 md:mb-16">
          <div className="max-w-2xl">
            <Badge variant="outline" className="mb-4 rounded-full px-3 py-1 bg-primary/10 text-primary border-primary/20 font-semibold uppercase tracking-wider text-xs">
              <MessageSquareHeart className="size-3.5 mr-1.5" />
              Client Impact
            </Badge>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white tracking-tighter mb-6 uppercase">
              WHAT OUR CLIENTS ARE SAYING:
            </h2>
            
            <div className="flex items-center gap-4 bg-card/30 backdrop-blur-sm border border-white/5 rounded-2xl p-4 inline-flex shadow-xl">
              <div className="flex flex-col items-center justify-center bg-primary/20 rounded-xl p-3 min-w-[80px]">
                <span className="text-3xl font-bold leading-none !text-[hsl(0,0%,100%)]">5.0</span>
              </div>
              <div>
                <div className="flex items-center gap-1 mb-1">
                  {[...Array(5)].map((_, i) => <Star key={i} data-index={i} className="size-5 fill-emerald-500 text-emerald-500" />)}
                </div>
                <p className="text-sm font-bold uppercase tracking-tight !text-[hsl(0,0%,100%)]">
                  Based on <strong className="!text-[hsl(0,0%,100%)]">83 reviews</strong> on Google
                </p>
              </div>
            </div>
          </div>

          <div className="hidden md:block">
            <Button asChild variant="default" className="rounded-full px-8 bg-linear-to-r from-primary to-secondary text-primary-foreground font-bold hover:scale-105 transition-all duration-300 shadow-lg shadow-primary/20">
              
            </Button>
          </div>
        </div>

        <Carousel opts={{
        align: "start",
        loop: true
      }} className="w-full">
          <CarouselContent className="-ml-4 md:-ml-6">
            {reviews.map((review, index) => <CarouselItem key={review.id} data-index={index} className="pl-4 md:pl-6 md:basis-1/2 lg:basis-1/3">
                <div className="h-full pb-4">
                  <ReviewCard review={review} index={index} />
                </div>
              </CarouselItem>)}
          </CarouselContent>
          <div className="flex items-center justify-center gap-4 mt-8 md:hidden">
            <CarouselPrevious className="static translate-y-0 translate-x-0 bg-card border-white/10 text-primary" />
            <CarouselNext className="static translate-y-0 translate-x-0 bg-card border-white/10 text-primary" />
          </div>
          <div className="hidden md:block">
            <CarouselPrevious className="-left-12 bg-card/80 backdrop-blur-sm border-white/10 text-primary hover:bg-primary hover:text-primary-foreground transition-all" />
            <CarouselNext className="-right-12 bg-card/80 backdrop-blur-sm border-white/10 text-primary hover:bg-primary hover:text-primary-foreground transition-all" />
          </div>
        </Carousel>

        <div className="mt-10 text-center md:hidden">
          <Button asChild variant="default" className="rounded-full px-8 w-full sm:w-auto bg-linear-to-r from-primary to-secondary text-primary-foreground font-bold hover:scale-105 transition-all duration-300 shadow-lg shadow-primary/20">
            <Link to="/ContactPage">Request a Free Quote</Link>
          </Button>
        </div>
      </div>
    </section>;
}